import { useEffect, useMemo, useRef, useState } from "react"

import { App, Drawer, Flex, Spin, Tabs, theme, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router"
import { useNavigate } from "react-router-dom"

import ButtonComponent from "../../../../Components/Button"
import Private from "../../../../Components/Private"
import Table from "../../../../Components/Table"
import TourComponent from "../../../../Components/Tour"
import { useExternalDemoMode } from "../../../../hooks/useExternalDemoMode"
import Icon from "../../../../Icon"
import utils, { getTourEl } from "../../../../Utils/utils"
import { setDemoTourStep as setDemoTourStepAction } from "../../../User/redux/user.actions"
import Creators from "../../reducer"
import { useAlertsPageQuery } from "../../useAlertsPageQuery"
import { AlertStatusInfo } from "../AlertStatusInfo/AlertStatusInfo"
import { CreateTicketModal } from "../CreateTicketModal"
import { CreditRequestModal } from "../CreditRequestModal"
import LostLegacyNotification from "../LostLegacyNotification/LostLegacyNotification"
import { OverviewInfo } from "../OverviewInfo"
import { getLogsColumns, HighlightStatusText } from "../Table/columns"

import "./styles.less"

const { Text } = Typography

export const AlertDrawer = ({ isDemo, isDemoModeBlocked, ...props }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { notification } = App.useApp()
  const { token } = theme.useToken()
  const { t } = useTranslation()
  const { pathname, search } = useLocation()
  const { openAlertID, business_account_id } = useAlertsPageQuery(search)
  const externalDemoMode = useExternalDemoMode()

  const { fetching, alertDetails, alertLogsFetching, alertLogs, saveFetching, saveResult } =
    useSelector((state) => state.alerts)
  const isMobile = useSelector((state) => state.nav.isMobile)
  const isBusinessAccountAdministrator = useSelector(
    (state) => state.auth.userInfo?.ba_role?.name === "Administrator"
  )
  const isDemoRestricted = isDemo && !isBusinessAccountAdministrator

  const showLegacyLostNotification = alertDetails?.show_legacy_lost_notification

  const open = !!openAlertID && !!alertDetails

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    placement: isMobile ? "topRight" : "topRight",
    style: isMobile ? { fontSize: 12 } : { minWidth: "max-content" }
  }

  useEffect(() => {
    if (openAlertID) {
      dispatch(Creators.getAlertDetailsRequest(openAlertID))
      dispatch(Creators.getAlertLogsRequest(openAlertID))
    }
  }, [openAlertID, saveResult])

  // Cleanup alerts details and logs if old values are present in persist store
  useEffect(() => {
    if (!openAlertID) {
      dispatch(Creators.alertsSetProp("alertDetails", null))
      dispatch(Creators.alertsSetProp("alertLogs", []))
    }
  }, [])

  const handleCloseModal = () => {
    if (business_account_id) {
      navigate(`${pathname}?business_account_id=${business_account_id}`, { replace: true })
    } else {
      navigate(pathname, { replace: true })
    }
    dispatch(Creators.alertsSetProp("alertDetails", null))
  }

  const copy = () => {
    if (alertDetails) {
      let url = process.env.REACT_APP_BASE_URI + pathname + search
      utils.copyToClipboard(url)
      notification.success({
        message: t("Link has been copied to the clipboard"),
        ...notificationConfig
      })
    }
  }

  const handleExport = () => {
    dispatch(Creators.getAlertDetailsExportRequest(alertDetails?.alert_id))
  }

  const getTableColumns = useMemo(() => getLogsColumns({ t }), [])

  const { demoTourStep } = useSelector((state) => state.user)

  const setDemoTourStep = (step) => {
    dispatch(setDemoTourStepAction(step))
  }

  const [showTour, setShowTour] = useState(false)

  useEffect(() => {
    setShowTour(false)
  }, [open])

  useEffect(() => {
    let timer
    if (open && demoTourStep === 3) {
      timer = setTimeout(() => setShowTour(true), 1000)
    }
    return () => clearTimeout(timer)
  }, [demoTourStep, open])

  const overviewRef = useRef(null)
  const exportRef = useRef(null)
  const viewAllInfoRef = useRef(null)
  const logsRef = useRef(null)

  const steps = useMemo(
    () => [
      {
        title: <Text type="secondary">Download full alert info</Text>,
        target: () => exportRef.current
      },
      {
        title: <Text type="secondary">View the alert’s change and action history</Text>,
        target: () => logsRef.current
      },
      {
        title: (
          <Text type="secondary">
            Easily request a <Text>refund </Text> for <Text>any </Text> alert
          </Text>
        ),
        target: () => getTourEl("credit-request-btn"),
        nextButtonProps: {
          onClick: () => {
            navigate("/configure/enrollment")
            setDemoTourStep(4)
            setShowTour(false)
            dispatch(Creators.alertsSetProp("alertDetails", null))
          },
          children: <text>Next</text>
        }
      }
    ],
    []
  )

  const items = [
    {
      key: "Overview",
      label: "Overview",
      children: (
        <div className="alert-drawer-tab-content" ref={overviewRef}>
          <Spin spinning={fetching} wrapperClassName="visible">
            <OverviewInfo
              data={alertDetails}
              type="drawer"
              onOpenTransactionModal={props.onOpenTransactionModal}
              onSubmitAlertStatusChange={props.onSubmitAlertStatusChange}
            />
          </Spin>
        </div>
      )
    },
    {
      key: "Logs",
      label: <span ref={logsRef}>Logs</span>,
      children: (
        <div className="alert-drawer-tab-content">
          {isMobile ? (
            <Spin spinning={alertLogsFetching}>
              <Flex vertical={true} gap={8}>
                {alertLogs?.map((log, idx) => (
                  <div key={log.created || idx} className="alert-log-card">
                    <HighlightStatusText text={log.log_message} />
                    <Text type="secondary" className="alert-log-card-date">
                      {dayjs(log.created).format("DD MMM YYYY h:mm A [(UTC)]")}
                    </Text>
                  </div>
                ))}
              </Flex>
            </Spin>
          ) : (
            <Table
              columns={getTableColumns}
              rowKey={(record) => `${record.log_message}-${record.created}`}
              loading={alertLogsFetching}
              dataSource={alertLogs}
              scroll={{ x: "auto" }}
              showSizeChanger={false}
              withOutPagination={true}
            />
          )}
        </div>
      )
    }
  ]

  const goDetailsPage = () => {
    if (pathname?.includes("admin/alerts") && business_account_id) {
      navigate(
        `/admin/alerts/details/${alertDetails?.alert_id || openAlertID}?business_account_id=${business_account_id}`
      )
    } else {
      navigate(`/alerts/details/${alertDetails?.alert_id || openAlertID}`)
    }
  }

  const mobileFooterContent = (
    <Flex gap={4} vertical={true} style={{ padding: "8px 16px 16px" }}>
      <Flex
        align="center"
        justify="space-between"
        style={{
          width: "100%",
          padding: 12,
          border: `1px solid`,
          borderColor: token?.colorBorder,
          borderRadius: token?.radiusBase,
          flexDirection: isMobile && "column",
          gap: isMobile && 12,
          alignItems: isMobile && "flex-start"
        }}
        className="alert-footer-card"
      >
        <Text strong>{t("Get a refund for any alert")}</Text>
        <CreditRequestModal
          isDemo={isDemoRestricted}
          tourElId={"credit-request-btn"}
          alert_id={openAlertID}
          fullWidth={isMobile}
        />
      </Flex>
      <Flex
        align="center"
        justify="space-between"
        style={{
          width: "100%",
          padding: 12,
          border: `1px solid`,
          borderColor: token?.colorBorder,
          borderRadius: token?.radiusBase,
          flexDirection: isMobile && "column",
          gap: isMobile && 12,
          alignItems: isMobile && "flex-start"
        }}
        className="alert-footer-card"
      >
        <Text strong>{t("Experiencing troubles?")}</Text>
        <CreateTicketModal
          isDemo={isDemoRestricted}
          externalDemoMode={externalDemoMode}
          fullWidth={isMobile}
        />
      </Flex>
      <AlertStatusInfo
        status={alertDetails?.alert_status}
        credit_request_id={alertDetails?.credit_request_id}
      />
    </Flex>
  )

  return (
    <Drawer
      title={t("Alert details & activity")}
      getContainer={() => document.getElementById("main-layout")}
      width={600}
      placement="right"
      closable={false}
      onClose={handleCloseModal}
      open={!!open}
      destroyOnClose={true}
      styles={{
        content: { borderRadius: isMobile ? 0 : "24px" },
        header: { padding: !isMobile ? "16px 24px" : "16px", border: "none" },
        footer: { padding: "8px 24px 24px", border: "none" }
      }}
      rootClassName="drawer-like-modal alert-details-drawer-root"
      extra={
        <ButtonComponent
          type="link"
          shape="circle"
          onClick={handleCloseModal}
          icon={<Icon type="cross_small" style={{ color: "#7A7A83" }} />}
        />
      }
      footer={
        isMobile ? null : (
          <Flex gap={8} vertical={true}>
            <Flex
              align="center"
              justify="space-between"
              style={{
                width: "100%",
                padding: 12,
                border: `1px solid`,
                borderColor: token?.colorBorder,
                borderRadius: token?.radiusBase
              }}
            >
              <Text strong>{t("Need help or credit request?")}</Text>
              <CreditRequestModal
                isDemo={isDemoRestricted || isDemoModeBlocked}
                tourElId={"credit-request-btn"}
                alert_id={openAlertID}
              />
              <CreateTicketModal isDemo={isDemoRestricted} externalDemoMode={externalDemoMode} />
            </Flex>

            <AlertStatusInfo
              status={alertDetails?.alert_status}
              credit_request_id={alertDetails?.credit_request_id}
            />
          </Flex>
        )
      }
    >
      {showLegacyLostNotification && <LostLegacyNotification fullWidth />}
      <Flex vertical={true} gap={12} className="alert-drawer-content">
        <Flex
          align={isMobile ? "flex-start" : "center"}
          vertical={isMobile}
          justify="space-between"
          gap={isMobile ? 8 : 0}
        >
          <Flex vertical={true} gap={4}>
            <Text type="secondary">{t("Alert ID")}</Text>
            <Text
              strong
              ellipsis={{ tooltip: alertDetails?.alert_id }}
              style={{
                maxWidth: isMobile ? "100%" : "310px",
                fontSize: isMobile ? 16 : 20,
                lineHeight: isMobile ? "20px" : 1.4
              }}
            >
              <Private>{alertDetails?.alert_id || openAlertID}</Private>
            </Text>
          </Flex>

          <Flex
            gap={8}
            style={{
              width: isMobile && "100%",
              minWidth: !isMobile && 238,
              flexDirection: isMobile && "row-reverse"
            }}
          >
            <Tooltip title={t("Copy alert link")}>
              <ButtonComponent
                onClick={copy}
                icon={<Icon type="link" />}
                style={{ width: isMobile ? 36 : 32 }}
                disabled={externalDemoMode}
              />
            </Tooltip>
            <Tooltip title={t("Export alert")}>
              <ButtonComponent
                ref={exportRef}
                onClick={handleExport}
                icon={<Icon type="arrow_inbox" />}
                style={{ width: isMobile ? 36 : 32 }}
                loading={saveFetching}
                disabled={isDemoRestricted || externalDemoMode || isDemoModeBlocked}
                data-tour="download-alert-info"
              />
            </Tooltip>
            <ButtonComponent
              ref={viewAllInfoRef}
              onClick={goDetailsPage}
              text={t("View full information")}
              style={isMobile ? { flex: 1 } : {}}
              data-tour="view-full-info"
            />
          </Flex>
        </Flex>

        <Tabs defaultActiveKey="Overview" items={items} />
        <TourComponent
          open={demoTourStep === 3 && showTour}
          steps={steps}
          onClose={() => {
            setDemoTourStep(null)
            setShowTour(false)
          }}
          getPopupContainer={() => document.body}
        />
      </Flex>
      {isMobile && mobileFooterContent}
    </Drawer>
  )
}
